import {
  Building2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Container,
  Info,
  ShieldCheck,
  Truck,
  Users,
} from 'lucide-react'
import { forwardRef, type ReactNode, useCallback, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Marker, MarkerContent, MarkerIcon } from '@/components/ui/marker'
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsBelowLg } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'
import { SummaryPopoverProvider } from './summary/summaryPopover'
import { useRevealAfterLoad } from './useRevealAfterLoad'

interface ConnectionStatus {
  /** Integration category label, e.g. "Telematics" or "ELD". */
  label: string
  /** Provider name once connected — omit while unconnected to show the connect action instead. */
  connectedTo?: string
}

export type MissingDataMessage = 'Loss runs pending' | 'Information needed'

interface SummarySection {
  key: string
  label: string
  icon?: ReactNode
  connectionStatus?: ConnectionStatus
  /** Item count shown as a badge, e.g. number of drivers/trucks/trailers on file. */
  count?: number
  /** The count is still being fetched: the badge holds its place instead of popping in. */
  countLoading?: boolean
  /** Missing-data reason shown below the icon/title, e.g. while a section has nothing on file yet. The card stays active — it's where the data goes in. */
  missingDataMessage?: MissingDataMessage
  /** What that reason means for this section, shown in the tooltip beside it. */
  missingDataTooltip?: string
  /** Work in flight on this section's data, shown in place of the missing-data reason. */
  processingMessage?: string
  /** Replaces the default "Connect provider" button — e.g. with the Terminal link. */
  connectAction?: ReactNode
  /** Keeps the missing-data line's space while there's no line yet, with the title centered in it. */
  holdsMissingDataLine?: boolean
  /** Regular-weight title, for the lighter detail cards. */
  plainLabel?: boolean
  /** Opens a dialog instead of a popover — for detail big enough to need the room. */
  asDialog?: boolean
}

/**
 * Fleet/claims data for the summary cards. A count left undefined means we don't have that
 * number for this application, and the card is dropped rather than shown with a made-up figure.
 */
export interface ApplicationSummaryData {
  driverCount?: number
  truckCount?: number
  trailerCount?: number
  /** Still fetching the fleet, so its cards show with a placeholder count rather than arriving late. */
  fleetCountsLoading?: boolean
  eldConnectedTo?: string
  telematicsConnectedTo?: string
  /** Shows the claims-history card at all. Independent of whether loss runs have landed. */
  showClaimsHistory?: boolean
  /** Claims parsed out of the loss runs, badged like the fleet counts. */
  claimsCount?: number
  /** What the claims-history card is short of, if anything — its missing-data line. */
  claimsMissingData?: MissingDataMessage
  /** Loss runs are on file and being read — claims will land on their own, so say so. */
  claimsParsing?: boolean
  /**
   * Real content per section key, rendered inside that section's popover or dialog.
   * A section with no entry falls back to the placeholder note.
   */
  content?: Partial<Record<SummarySectionKey, ReactNode>>
  /** Connect action per section, replacing the default "Connect provider" button. */
  connectActions?: Partial<Record<SummarySectionKey, ReactNode>>
}

/** Query param the roster pages use to open one summary card on arrival. */
export const SUMMARY_SECTION_PARAM = 'section'

export type SummarySectionKey =
  | 'coverage-needs'
  | 'business-details'
  | 'drivers'
  | 'trucks'
  | 'trailers'
  | 'claims-history'
  | 'my-documents'
  | 'additional-info'

/** The quotes page, landing on one summary card already open. */
export function quotesSectionHref(section: SummarySectionKey): string {
  return `/quotes?${SUMMARY_SECTION_PARAM}=${section}`
}

// Rarely change once set, so they sit above the rest as a lighter-weight,
// borderless row instead of a bordered card.
const STABLE_SECTIONS: SummarySection[] = [
  { key: 'coverage-needs', label: 'Coverage needs', icon: <ShieldCheck /> },
  { key: 'business-details', label: 'Business details', icon: <Building2 /> },
]

// Keyed to the claims card, not shared. "Information needed" gets no tooltip.
const CLAIMS_MISSING_DATA_TOOLTIPS: Partial<Record<MissingDataMessage, string>> = {
  // Covers a request nobody had an address to send as well as one a carrier hasn't answered.
  'Loss runs pending': 'Cai is still collecting this from your current carrier.',
}

/** Short of data with nothing being read yet — the mobile panel's red dot. */
function needsAttention(section: SummarySection): boolean {
  return section.missingDataMessage != null && section.processingMessage == null
}

function buildCardSections(data: ApplicationSummaryData): SummarySection[] {
  const sections: SummarySection[] = []

  if (data.driverCount !== undefined) {
    sections.push({
      key: 'drivers',
      label: 'Drivers',
      icon: <Users />,
      connectionStatus: { label: 'ELD', connectedTo: data.eldConnectedTo },
      connectAction: data.connectActions?.drivers,
      count: data.driverCount,
    })
  }
  if (data.truckCount !== undefined || data.fleetCountsLoading) {
    sections.push({
      key: 'trucks',
      label: 'Trucks',
      icon: <Truck />,
      connectionStatus: { label: 'Telematics', connectedTo: data.telematicsConnectedTo },
      connectAction: data.connectActions?.trucks,
      count: data.truckCount,
      countLoading: data.truckCount === undefined,
    })
  }
  if (data.trailerCount !== undefined || data.fleetCountsLoading) {
    sections.push({
      key: 'trailers',
      label: 'Trailers',
      icon: <Container />,
      count: data.trailerCount,
      countLoading: data.trailerCount === undefined,
    })
  }
  if (data.showClaimsHistory ?? data.claimsMissingData !== undefined) {
    sections.push({
      key: 'claims-history',
      label: 'Claims history',
      icon: <ClipboardList />,
      count: data.claimsCount,
      missingDataMessage: data.claimsMissingData,
      missingDataTooltip: data.claimsMissingData
        ? CLAIMS_MISSING_DATA_TOOLTIPS[data.claimsMissingData]
        : undefined,
      processingMessage: data.claimsParsing ? 'Reading loss runs' : undefined,
      // Always holds its line, so loading, landing and clearing never resize the row.
      holdsMissingDataLine: true,
    })
  }

  return sections
}

// The right-hand pair. Documents renders a full table so it needs a dialog;
// Additional info is a short list and opens in place, like the fleet cards.
const DETAIL_SECTIONS: SummarySection[] = [
  { key: 'my-documents', label: 'Documents', plainLabel: true, asDialog: true },
  { key: 'additional-info', label: 'Additional info', plainLabel: true },
]

/**
 * Which application details a customer goes back and checks before picking a quote — the
 * summary's whole reason for sitting above the cards.
 */
function useTrackedSectionToggle(section: SummarySection, setIsOpen?: (open: boolean) => void) {
  const track = useTrackQuotesEvent()
  return (open: boolean) => {
    track(open ? QuotesEvent.SUMMARY_SECTION_OPENED : QuotesEvent.SUMMARY_SECTION_CLOSED, {
      section: section.key,
      item_count: section.count ?? null,
      missing_data: section.missingDataMessage ?? null,
      connected_to: section.connectionStatus?.connectedTo ?? null,
    })
    setIsOpen?.(open)
  }
}

/** The stand-in shown where a section has no real connect flow wired to it yet. */
function ConnectProviderPlaceholder({ section }: { section: string }) {
  const track = useTrackQuotesEvent()

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => track(QuotesEvent.SUMMARY_CONNECT_PROVIDER_CLICKED, { section })}
    >
      Connect provider
    </Button>
  )
}

function SummarySectionPopoverContent({
  section,
  content,
  readonly,
  close,
}: {
  section: SummarySection
  content?: ReactNode
  readonly?: boolean
  /** Handed to the editors inside, so dismissing one of their modals closes this popover. */
  close: () => void
}) {
  return (
    <PopoverContent
      align="start"
      side="bottom"
      collisionPadding={8}
      className="max-h-[70vh] w-96 max-w-[calc(100vw-1rem)] overflow-y-auto"
    >
      <PopoverHeader>
        <PopoverTitle>{readonly ? section.label : `Update ${section.label}`}</PopoverTitle>
      </PopoverHeader>
      {section.connectionStatus && (
        <div className="border-ink-100 mt-3 mb-5 flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
          <span className="text-ink-600">
            <span className="font-semibold">{section.connectionStatus.label}:</span>{' '}
            {section.connectionStatus.connectedTo
              ? `${section.connectionStatus.connectedTo}, Connected`
              : null}
          </span>
          {!section.connectionStatus.connectedTo &&
            (section.connectAction ??
              // The fallback is a placeholder with no handler, so read-only shows nothing at all.
              (readonly ? null : <ConnectProviderPlaceholder section={section.key} />))}
        </div>
      )}
      {/* The connection card already supplies the gap under the title; without one the
          content would sit flush against it. */}
      <div className={cn(!section.connectionStatus && 'mt-4')}>
        <SummaryPopoverProvider close={close}>
          {content ?? <PopoverDescription>Contents coming soon.</PopoverDescription>}
        </SummaryPopoverProvider>
      </div>
    </PopoverContent>
  )
}

/** Lighter-weight, borderless trigger for sections unlikely to change (Coverage needs, Business details). */
function StableSectionPopover({
  section,
  content,
  readonly,
}: {
  section: SummarySection
  content?: ReactNode
  readonly?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const close = useCallback(() => setIsOpen(false), [])
  const onOpenChange = useTrackedSectionToggle(section, setIsOpen)

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="text-ink-600 hover:text-ink gap-2">
          <span className="[&_svg]:size-4">{section.icon}</span>
          {section.label}
          <ChevronDown className="text-ink-300 size-4" />
        </Button>
      </PopoverTrigger>
      <SummarySectionPopoverContent section={section} content={content} readonly={readonly} close={close} />
    </Popover>
  )
}

/**
 * Bordered card button shared by the popover- and modal-triggered cards.
 * Forwards ref and all extra props — required for Radix's `asChild` to wire
 * up its own ref/onClick/aria-* onto the real button; without this, the
 * trigger silently stops responding to clicks.
 */
const CardTriggerButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { section: SummarySection }
>(({ section, className, ...props }, ref) => {
  // The line's space is reserved from the start so the row never resizes: the title begins
  // centered with the other cards, then slides up as the line fades in.
  const delaysMissingData = !section.processingMessage && section.missingDataMessage === 'Information needed'
  const revealed = useRevealAfterLoad(delaysMissingData)
  const holdsLine =
    !!section.holdsMissingDataLine && !section.missingDataMessage && !section.processingMessage
  const titleLowered = holdsLine || !revealed
  const missingDataLine = (
    <span
      className={cn(
        'text-ink-400 flex items-center gap-1 text-xs font-normal',
        // Mobile's Needs info badge already says it.
        delaysMissingData &&
          'transition-opacity duration-300 ease-out motion-reduce:transition-none max-lg:hidden',
        !revealed && 'opacity-0'
      )}
    >
      {section.missingDataMessage}
      {section.missingDataTooltip && (
        <Tooltip>
          {/* span, not a button — this sits inside the card's own <button> trigger */}
          <TooltipTrigger asChild>
            <span tabIndex={-1} aria-label={`Why ${section.label.toLowerCase()} needs this`}>
              <Info className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{section.missingDataTooltip}</TooltipContent>
        </Tooltip>
      )}
    </span>
  )

  return (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      className={cn(
        'group border-ink-100 h-auto flex-col items-start gap-1 rounded-xl border bg-transparent px-4 py-4 text-left whitespace-nowrap',
        // Mobile shows the same card as desktop, stacked full width. It used to squeeze
        // the fleet cards into one row as icon + count, which left the counts unlabelled
        // and the claims card with nowhere to say what it was waiting for.
        'max-lg:w-full',
        // Desktop cards share the row equally so it fills the container.
        'lg:flex-1',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'flex w-full items-center justify-between gap-2.5',
          (delaysMissingData || holdsLine) &&
            'transition-transform duration-300 ease-out motion-reduce:transition-none',
          // Half the missing-data line (16px) plus the gap (4px) above it.
          titleLowered && (delaysMissingData ? 'lg:translate-y-2.5' : 'translate-y-2.5')
        )}
      >
        <span className="flex items-center gap-2.5">
          {section.icon && (
            <span className="text-ink-500 group-hover:text-ink [&_svg]:size-5">{section.icon}</span>
          )}
          <span className={cn('text-ink text-sm', section.plainLabel ? 'font-normal' : 'font-bold')}>
            {section.label}
          </span>
          {section.countLoading ? (
            <Skeleton className="h-5 w-5 rounded-full" />
          ) : (
            section.count !== undefined && (
              <Badge
                variant="secondary"
                className="group-hover:bg-brand-800/15 h-5 min-w-5 px-1 py-0 tabular-nums"
              >
                {section.count}
              </Badge>
            )
          )}
          {/* Not while loss runs are pending: that wait is on the carrier, not the customer. */}
          {needsAttention(section) && section.missingDataMessage === 'Information needed' && (
            <Badge variant="warning" className="lg:hidden">
              Needs info
            </Badge>
          )}
        </span>
        {/* Radix's data-state turns it up while the card is open. */}
        <ChevronDown className="text-ink-300 size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none" />
      </span>
      {/* Takes the missing-data line's place: the data has arrived, it just isn't readable yet. */}
      {section.processingMessage ? (
        <Marker role="status" className="text-ink-400 gap-1.5 text-xs font-normal">
          <MarkerIcon>
            <Spinner aria-hidden className="size-3.5" />
          </MarkerIcon>
          <MarkerContent>{section.processingMessage}</MarkerContent>
        </Marker>
      ) : section.missingDataMessage ? (
        missingDataLine
      ) : (
        holdsLine && <span aria-hidden className="h-4" />
      )}
    </Button>
  )
})
CardTriggerButton.displayName = 'CardTriggerButton'

/** Bordered card trigger for sections with live data (counts, connection status, missing-data states). */
function CardSectionPopover({
  section,
  content,
  readonly,
  defaultOpen,
}: {
  section: SummarySection
  content?: ReactNode
  readonly?: boolean
  /** Opens the card on mount — how a locked roster page lands the customer on its own section. */
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(!!defaultOpen)
  const close = useCallback(() => setIsOpen(false), [])
  const onOpenChange = useTrackedSectionToggle(section, setIsOpen)

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <CardTriggerButton section={section} />
      </PopoverTrigger>
      <SummarySectionPopoverContent section={section} content={content} readonly={readonly} close={close} />
    </Popover>
  )
}

/** Bordered card trigger that opens a modal instead of a popover, for the larger detail views. */
function CardSectionDialog({ section, content }: { section: SummarySection; content?: ReactNode }) {
  const onOpenChange = useTrackedSectionToggle(section)

  return (
    <Dialog onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <CardTriggerButton section={section} />
      </DialogTrigger>
      <DialogContent size="xl" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{section.label}</DialogTitle>
          {!content && <DialogDescription>Details to follow.</DialogDescription>}
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Application summary shown at the top of the Quotes page — clickable sections, each
 * opening a popover (or dialog) with the underlying detail supplied via `data.content`.
 * Coverage needs/Business details rarely change, so on desktop they sit above the rest as a
 * lighter, borderless row; on mobile they head the card stack.
 */
export function ApplicationSummarySection({
  data,
  readonly,
  action,
  openSection,
}: {
  data: ApplicationSummaryData
  /** Nothing inside can be changed — the popovers title themselves rather than offering an update. */
  readonly?: boolean
  /** Trailing control for the Coverage needs / Business details row, pushed right on `lg`+. */
  action?: ReactNode
  /** Card to open on arrival — a locked roster page sends the customer straight to its own section. */
  openSection?: SummarySectionKey
}) {
  const track = useTrackQuotesEvent()
  const contentFor = (key: string) => data.content?.[key as SummarySectionKey]
  // Expander is mobile-only — on `lg`+ the body is always shown regardless of state.
  // Collapsed by default; the notification draws the eye when there's something to expand for.
  // A card asked for by name starts it expanded, or on mobile the card would be behind the collapse.
  const [isOpen, setIsOpen] = useState(!!openSection)
  // Per visit only: the notification returns on every load until claims no longer need attention.
  const [notificationSeen, setNotificationSeen] = useState(!!openSection)
  const isMobile = useIsBelowLg()

  const cardSections = buildCardSections(data)

  // Surfaces a notification on the collapsed mobile title when Claims history is still
  // waiting on data, so it isn't hidden behind the collapse. The card badges the same state.
  const claimsNeedsAttention = cardSections.some(
    (section) => section.key === 'claims-history' && needsAttention(section)
  )

  return (
    <section
      className={cn(
        // The top bleed matches the page's mobile mt-4, so the fill meets the broker bar.
        'max-lg:bg-ink-25 max-lg:-mx-3 max-lg:-mt-4 max-lg:rounded-b-xl max-lg:px-3 max-lg:pt-4'
      )}
    >
      {/* Mobile-only title row; the whole row is the expander toggle. */}
      <h2 className="mb-2 lg:hidden">
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-full justify-between px-0 hover:bg-transparent"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Hide application info' : 'Show application info'}
          onClick={() => {
            track(QuotesEvent.SUMMARY_TOGGLED, { opened: !isOpen })
            if (!isOpen) setNotificationSeen(true)
            setIsOpen((open) => !open)
          }}
        >
          <span className="flex items-center gap-1.5">
            <span className="text-ink-500 text-xs font-semibold tracking-wide uppercase">
              Application info
            </span>
            {claimsNeedsAttention && !notificationSeen && (
              // Sized like the nav cart's count; claims is the only section that can raise it.
              <Badge
                variant="destructive"
                role="status"
                aria-label="Needs attention"
                className="animate-in fade-in zoom-in-50 fill-mode-backwards h-4 min-w-4 px-1 py-0 text-[10px] leading-none font-bold delay-300 duration-300 motion-reduce:animate-none"
              >
                1
              </Badge>
            )}
          </span>
          {isOpen ? <ChevronUp className="text-ink-300" /> : <ChevronDown className="text-ink-300" />}
        </Button>
      </h2>
      {/* On mobile the stable sections move into the card stack, leaving this row to the action. */}
      <div
        className={cn(
          'mb-2 flex flex-col gap-1 lg:flex-row lg:flex-wrap lg:items-center',
          !action && 'max-lg:hidden'
        )}
      >
        {!isMobile &&
          STABLE_SECTIONS.map((section) => (
            <StableSectionPopover
              key={section.key}
              section={section}
              content={contentFor(section.key)}
              readonly={readonly}
            />
          ))}
        {action && <div className="max-lg:self-end lg:ml-auto">{action}</div>}
      </div>
      <div
        className={cn(
          'flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-stretch lg:gap-3',
          !isOpen && 'max-lg:hidden'
        )}
      >
        {/* On desktop `lg:contents` flattens this wrapper so the cards + divider +
            modals share one wrapping row; on mobile they stack. */}
        <div className="flex flex-col gap-2 lg:contents">
          {[...(isMobile ? STABLE_SECTIONS : []), ...cardSections].map((section) => (
            <CardSectionPopover
              key={section.key}
              section={section}
              content={contentFor(section.key)}
              readonly={readonly}
              defaultOpen={section.key === openSection}
            />
          ))}
        </div>
        <div className="bg-ink-100 w-px self-stretch max-lg:hidden" aria-hidden="true" />
        <div className="flex flex-col gap-2 lg:contents">
          {DETAIL_SECTIONS.map((section) =>
            section.asDialog ? (
              <CardSectionDialog key={section.key} section={section} content={contentFor(section.key)} />
            ) : (
              <CardSectionPopover
                key={section.key}
                section={section}
                content={contentFor(section.key)}
                readonly={readonly}
                defaultOpen={section.key === openSection}
              />
            )
          )}
        </div>
      </div>
      <div className={cn('border-ink-100 mt-4 border-t max-lg:-mx-4', isOpen && 'max-lg:border-ink-200')} />
    </section>
  )
}
